import * as THREE from 'three';
import { FullscreenPass, makeTarget } from '../core/Fullscreen';
import { commonGLSL } from '../shaders/common';
import { atmosphereGLSL, skyViewSampleGLSL } from '../shaders/atmosphere';
import { compositeFrag } from '../shaders/composite';
import { bloomDownFrag, bloomUpFrag, exposureFrag, finalFrag } from '../shaders/post';
import type { Atmosphere } from '../sky/Atmosphere';
import { shared } from './SharedUniforms';

const BLOOM_MIPS = 6;

/** Procedural Milky Way glow in equatorial (RA/Dec) equirectangular projection. */
function buildMilkyWay(): THREE.DataTexture {
  const W = 1024, H = 512;
  const data = new Uint8Array(W * H * 4);
  const D2R = Math.PI / 180;
  // galactic north pole and center (J2000)
  const raGP = 192.85948 * D2R, decGP = 27.12825 * D2R, lNCP = 122.93192 * D2R;
  const hash = (x: number, y: number) => {
    const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return s - Math.floor(s);
  };
  const vn = (x: number, y: number) => {
    const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
    const a = hash(xi, yi), b = hash(xi + 1, yi), c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  };
  const fbm = (x: number, y: number) => {
    let s = 0, a = 0.5;
    for (let i = 0; i < 6; i++) { s += a * vn(x, y); x *= 2.03; y *= 2.03; a *= 0.5; }
    return s;
  };
  for (let j = 0; j < H; j++) {
    const dec = ((j + 0.5) / H - 0.5) * Math.PI;
    for (let i = 0; i < W; i++) {
      const ra = ((i + 0.5) / W) * 2 * Math.PI;
      const sb = Math.sin(dec) * Math.sin(decGP) + Math.cos(dec) * Math.cos(decGP) * Math.cos(ra - raGP);
      const b = Math.asin(sb);
      const y = Math.cos(dec) * Math.sin(ra - raGP);
      const x = Math.sin(dec) * Math.cos(decGP) - Math.cos(dec) * Math.sin(decGP) * Math.cos(ra - raGP);
      let l = lNCP - Math.atan2(y, x);
      l = ((l % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      const lc = l > Math.PI ? l - 2 * Math.PI : l; // -PI..PI, 0 = galactic center
      const bulge = Math.exp(-(lc * lc) / (2 * 0.3 * 0.3));
      const width = 0.09 + 0.08 * bulge + 0.03 * fbm(l * 2 + 9, 1.3);
      const band = Math.exp(-(b * b) / (2 * width * width));
      // clumpy star clouds
      const n = fbm(l * 7, b * 18);
      const clouds = Math.pow(Math.max(0, n * 1.6 - 0.35), 1.6);
      const speck = fbm(l * 60 + 2, b * 160 + 5);
      // dust: Great Rift (Cygnus -> Sagittarius) + filamentary lanes
      const riftCenter = 0.012 * Math.sin(l * 3) + 0.03 * (fbm(l * 4 + 4, 3) - 0.5);
      const riftW = 0.018 + 0.02 * fbm(l * 6 + 1, 7);
      const rift = Math.exp(-((b - riftCenter) ** 2) / (2 * riftW * riftW)) * (lc > -0.2 && lc < 1.4 ? 1 : 0.35);
      const ridged = 1 - Math.abs(fbm(l * 16 + 7, b * 40 + 3) * 2 - 1);
      const lanes = Math.pow(Math.max(0, ridged), 6) * band;
      let I = band * (0.18 + 0.82 * clouds) * (0.55 + 0.9 * bulge) * (0.75 + 0.5 * speck);
      I *= (1 - 0.8 * rift) * (1 - 0.6 * lanes);
      I += bulge * Math.exp(-(b * b) / (2 * 0.12 * 0.12)) * 0.22 * (0.6 + 0.8 * clouds);
      I = Math.max(0, I) * 0.85;
      const warm = bulge * 0.6;
      const k = (j * W + i) * 4;
      data[k] = Math.min(255, Math.round(255 * I * (0.85 + 0.25 * warm)));
      data[k + 1] = Math.min(255, Math.round(255 * I * (0.86 + 0.08 * warm)));
      data[k + 2] = Math.min(255, Math.round(255 * I * (0.95 - 0.2 * warm)));
      data[k + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(data, W, H, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.RepeatWrapping;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  return t;
}

export interface PipelineSettings {
  renderScale: number;
  msaa: number;
  bloomStrength: number;
}

export class Pipeline {
  sceneRT!: THREE.WebGLRenderTarget;
  compositeRT!: THREE.WebGLRenderTarget;
  private bloomRTs: THREE.WebGLRenderTarget[] = [];
  private bloomUpRTs: THREE.WebGLRenderTarget[] = [];
  private expRTs = [makeTarget(1, 1, { type: THREE.FloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter }),
    makeTarget(1, 1, { type: THREE.FloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter })];
  private expIdx = 0;
  private readonly composite: FullscreenPass;
  private readonly down: FullscreenPass;
  private readonly up: FullscreenPass;
  private readonly exposure: FullscreenPass;
  private readonly final: FullscreenPass;
  readonly compositeUniforms: Record<string, THREE.IUniform>;
  readonly finalUniforms: Record<string, THREE.IUniform>;
  width = 1;
  height = 1;
  private resetExposure = true;
  exposureBias = 0;
  private readonly blackClouds: THREE.DataTexture;

  constructor(private readonly renderer: THREE.WebGLRenderer, atmo: Atmosphere, private settings: PipelineSettings) {
    this.blackClouds = new THREE.DataTexture(new Float32Array([0, 0, 0, 1]), 1, 1, THREE.RGBAFormat, THREE.FloatType);
    this.blackClouds.needsUpdate = true;
    this.compositeUniforms = {
      ...atmo.uniforms,
      uSceneColor: { value: null },
      uSceneDepth: { value: null },
      uClouds: { value: this.blackClouds },
      uMilkyWay: { value: buildMilkyWay() },
      uInvProjView: { value: new THREE.Matrix4() },
      uCamPos: { value: new THREE.Vector3() },
      uMoonIllum: { value: 1 },
      uMoonLitDir: { value: new THREE.Vector3(0, 1, 0) },
      uFog: { value: new THREE.Vector4(0.0, 2400, 400, 0) },
      uValley: { value: new THREE.Vector4(2330, 60, 30, 0) },
      uFogAlbedo: { value: new THREE.Vector3(0.95, 0.95, 0.96) },
      uPrecipHaze: { value: 0 },
      uLST: { value: 0 },
      uLat: { value: THREE.MathUtils.degToRad(37.6228) },
      uStarVis: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uCloudsOn: { value: 0 },
      uLightning: { value: 0 },
      uLightningPos: { value: new THREE.Vector3() },
      uCurvOrigin: { value: new THREE.Vector3() },
      uCloudFar: shared.uCloudFar,
    };
    const prefix = commonGLSL + atmosphereGLSL + skyViewSampleGLSL;
    this.composite = new FullscreenPass(compositeFrag, this.compositeUniforms, {}, prefix);
    this.down = new FullscreenPass(bloomDownFrag, { uSrc: { value: null }, uTexel: { value: new THREE.Vector2() }, uFirst: { value: 0 } });
    this.up = new FullscreenPass(bloomUpFrag, { uSrc: { value: null }, uTexel: { value: new THREE.Vector2() }, uRadius: { value: 1 } });
    this.up.material.blending = THREE.AdditiveBlending;
    this.up.material.transparent = true;
    this.exposure = new FullscreenPass(exposureFrag, {
      uSrc: { value: null }, uPrev: { value: null }, uSrcSize: { value: new THREE.Vector2() },
      uDt: { value: 0.016 }, uBias: { value: 0 }, uReset: { value: 1 },
    }, {}, commonGLSL);
    this.finalUniforms = {
      uHDR: { value: null }, uBloom: { value: null }, uExposure: { value: null },
      uBloomStrength: { value: settings.bloomStrength }, uTime: { value: 0 }, uResolution: { value: new THREE.Vector2() },
      uSaturation: { value: 1.12 }, uContrast: { value: 1.05 }, uVignette: { value: 0.22 }, uGrain: { value: 0.012 }, uTonemap: { value: 0 },
    };
    this.final = new FullscreenPass(finalFrag, this.finalUniforms, {}, commonGLSL);
  }

  setSettings(s: PipelineSettings) {
    const rebuild = s.msaa !== this.settings.msaa || s.renderScale !== this.settings.renderScale;
    this.settings = s;
    this.finalUniforms.uBloomStrength.value = s.bloomStrength;
    if (rebuild) this.resize(this.width, this.height);
  }

  resize(w: number, h: number) {
    this.width = w;
    this.height = h;
    const sw = Math.max(1, Math.round(w * this.settings.renderScale));
    const sh = Math.max(1, Math.round(h * this.settings.renderScale));
    this.sceneRT?.dispose();
    this.compositeRT?.dispose();
    const depth = new THREE.DepthTexture(sw, sh, THREE.FloatType);
    depth.format = THREE.DepthFormat;
    depth.minFilter = depth.magFilter = THREE.NearestFilter;
    this.sceneRT = new THREE.WebGLRenderTarget(sw, sh, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      samples: this.settings.msaa,
      depthBuffer: true,
      depthTexture: depth,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      generateMipmaps: false,
    });
    this.compositeRT = makeTarget(sw, sh);
    for (const t of this.bloomRTs) t.dispose();
    for (const t of this.bloomUpRTs) t.dispose();
    this.bloomRTs = [];
    this.bloomUpRTs = [];
    let bw = sw, bh = sh;
    for (let i = 0; i < BLOOM_MIPS; i++) {
      bw = Math.max(1, Math.floor(bw / 2));
      bh = Math.max(1, Math.floor(bh / 2));
      this.bloomRTs.push(makeTarget(bw, bh));
      this.bloomUpRTs.push(makeTarget(bw, bh));
    }
    this.compositeUniforms.uResolution.value.set(sw, sh);
    this.finalUniforms.uResolution.value.set(w, h);
  }

  get depthTexture(): THREE.DepthTexture {
    return this.sceneRT.depthTexture as THREE.DepthTexture;
  }

  setClouds(tex: THREE.Texture | null, on: boolean) {
    this.compositeUniforms.uClouds.value = tex ?? this.blackClouds;
    this.compositeUniforms.uCloudsOn.value = on ? 1 : 0;
  }

  renderScene(scene: THREE.Scene, camera: THREE.Camera) {
    const r = this.renderer;
    r.setRenderTarget(this.sceneRT);
    r.setClearColor(0x000000, 0);
    r.clear(true, true, true);
    r.render(scene, camera);
  }

  renderComposite(camera: THREE.PerspectiveCamera) {
    const u = this.compositeUniforms;
    u.uSceneColor.value = this.sceneRT.texture;
    u.uSceneDepth.value = this.sceneRT.depthTexture;
    u.uInvProjView.value.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).invert();
    u.uCamPos.value.copy(camera.position);
    this.composite.render(this.renderer, this.compositeRT);
  }

  /** Bloom, exposure and the final tone-mapped output to the canvas. */
  renderPost(dt: number, time: number) {
    const r = this.renderer;
    const prevAuto = r.autoClear;
    // bloom down
    let src: THREE.Texture = this.compositeRT.texture;
    let sw = this.compositeRT.width, sh = this.compositeRT.height;
    for (let i = 0; i < BLOOM_MIPS; i++) {
      this.down.material.uniforms.uSrc.value = src;
      this.down.material.uniforms.uTexel.value.set(1 / sw, 1 / sh);
      this.down.material.uniforms.uFirst.value = i === 0 ? 1 : 0;
      this.down.render(r, this.bloomRTs[i]);
      src = this.bloomRTs[i].texture;
      sw = this.bloomRTs[i].width; sh = this.bloomRTs[i].height;
    }
    // bloom up (accumulate into up chain)
    r.autoClear = false;
    for (let i = BLOOM_MIPS - 1; i > 0; i--) {
      const dst = this.bloomUpRTs[i - 1];
      // copy base (down level i-1) then add upsampled
      this.up.material.blending = THREE.NoBlending;
      this.up.material.uniforms.uSrc.value = this.bloomRTs[i - 1].texture;
      this.up.material.uniforms.uRadius.value = 0.0;
      this.up.render(r, dst);
      this.up.material.blending = THREE.AdditiveBlending;
      const s = i === BLOOM_MIPS - 1 ? this.bloomRTs[i] : this.bloomUpRTs[i];
      this.up.material.uniforms.uSrc.value = s.texture;
      this.up.material.uniforms.uTexel.value.set(1 / s.width, 1 / s.height);
      this.up.material.uniforms.uRadius.value = 1.0;
      this.up.render(r, dst);
    }
    r.autoClear = prevAuto;
    // exposure from mip 4
    const small = this.bloomRTs[Math.min(4, BLOOM_MIPS - 1)];
    const eu = this.exposure.material.uniforms;
    eu.uSrc.value = small.texture;
    eu.uSrcSize.value.set(Math.min(small.width, 48), Math.min(small.height, 32));
    eu.uPrev.value = this.expRTs[this.expIdx].texture;
    eu.uDt.value = dt;
    eu.uBias.value = this.exposureBias;
    eu.uReset.value = this.resetExposure ? 1 : 0;
    this.resetExposure = false;
    this.expIdx = 1 - this.expIdx;
    this.exposure.render(r, this.expRTs[this.expIdx]);
    // final
    const fu = this.finalUniforms;
    fu.uHDR.value = this.compositeRT.texture;
    fu.uBloom.value = this.bloomUpRTs[0].texture;
    fu.uExposure.value = this.expRTs[this.expIdx].texture;
    fu.uTime.value = time;
    this.final.render(r, null);
  }

  snapExposure() {
    this.resetExposure = true;
  }
}
